import React, { useEffect, useCallback, useState } from "react";
import PropTypes from "prop-types";

import CustomImage from "../../atoms/customImage";
import Typography from "../../atoms/typography";
import { constants } from "../../../constants/pageConstants/pdpConstants";
import { convertTimestampStringToDate } from "../../../utils/common/deliveryDate";
import { clock } from "../../../utils/common/timeSlot";

/**
 * CountDownTimer Component
 *
 * Displays a countdown timer based on the provided delivery time details.
 * Dynamically updates until the specified cutoff timestamp.
 *
 * @param {Object} props.deliveryTimeLeft - Delivery time details.
 * @returns {React.ReactElement} The rendered CountDownTimer component.
 */
export const CountDownTimer = ({ deliveryTimeLeft }) => {
  const [countDown, setCountDown] = useState(null);
  const [timerId, setTimerId] = useState(null);

  /**
   * This method handles timer tick
   *
   * @param {string}  timer
   * @returns {void} set tier value to state
   */
  const timerTick = useCallback((timer) => {
    if (timer > 0) {
      clearTimeout(timerId);
      setCountDown(clock(timer));
      setTimerId(
        setTimeout(() => {
          timerTick(timer - 1000);
        }, 1000),
      );
    } else {
      clearTimeout(timerId);
    }
  }, []);

  useEffect(() => {
    const currentDateObj = convertTimestampStringToDate(deliveryTimeLeft?.currentTimestamp);
    const cutoffDateObj = convertTimestampStringToDate(deliveryTimeLeft?.cutoffTimestamp);

    if (!currentDateObj || !cutoffDateObj) {
      return;
    }
    const currentTime = currentDateObj.getTime();
    const cutoffTime = cutoffDateObj.getTime();
    if (timerId) {
      clearTimeout(timerId);
    }
    timerTick(cutoffTime - currentTime);
  }, [deliveryTimeLeft?.currentTimestamp, deliveryTimeLeft?.cutoffTimestamp]);

  return (
    countDown && (
      <div className="flex items-center justify-center gap-4 bg-blue-500 py-8 text-center text-12 md:bg-white-900 md:py-0 md:pb-8">
        <CustomImage src="/icons/clock.svg" width={16} height={16} isPriority={false} sizes="10vw" alt="search icons" />

        <Typography Tag="p" classes="text-12 font-500 text-grey-500">
          {constants.TIME_LEFT_TO_GET_DELIVERED}
        </Typography>
        <Typography Tag="p" classes="text-12 font-600 text-orange-100">
          {countDown}
        </Typography>
      </div>
    )
  );
};

CountDownTimer.propTypes = {
  deliveryTimeLeft: PropTypes.instanceOf(Object).isRequired,
};
